// packages/utils/src/shared/fetchWithTimeout.ts

/**
 * Thrown when {@link fetchWithTimeout} aborts a request because it exceeded
 * `timeoutMs`. Distinguishable from a caller-initiated abort so callers can
 * tell "the network stalled" apart from "we cancelled this on purpose".
 */
export class FetchTimeoutError extends Error {
  readonly timeoutMs: number;
  constructor(timeoutMs: number) {
    super(`Request timed out after ${timeoutMs}ms`);
    this.name = 'FetchTimeoutError';
    this.timeoutMs = timeoutMs;
  }
}

export interface FetchWithTimeoutInit extends RequestInit {
  /**
   * Abort the request after this many ms. A raw `fetch()` has **no** timeout —
   * on a stalled / lossy connection it can hang for minutes (until the OS-level
   * socket timeout) and freeze whatever awaits it. That hang is the difference
   * between "an upload failed, show an error" and "the whole tab is stuck".
   * Default 20000.
   */
  timeoutMs?: number;
}

/**
 * `fetch` that always bounds how long it can hang. On timeout it rejects with a
 * {@link FetchTimeoutError}; a caller-supplied `signal` still works and composes
 * with the internal timeout (whichever aborts first wins).
 *
 * Isomorphic — relies only on `fetch`, `AbortController`, and `setTimeout`,
 * which exist in browsers and Node 18+. Use this instead of bare `fetch()` for
 * any request whose failure should surface as an error rather than an
 * indefinite freeze.
 */
export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: FetchWithTimeoutInit = {},
): Promise<Response> {
  const { timeoutMs = 20_000, signal: callerSignal, ...rest } = init;

  const controller = new AbortController();

  // Fold a caller-provided signal into our controller so either source aborts.
  const onCallerAbort = () => controller.abort(callerSignal?.reason);
  if (callerSignal) {
    if (callerSignal.aborted) {
      controller.abort(callerSignal.reason);
    } else {
      callerSignal.addEventListener('abort', onCallerAbort, { once: true });
    }
  }

  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort(new FetchTimeoutError(timeoutMs));
  }, timeoutMs);

  try {
    return await fetch(input, { ...rest, signal: controller.signal });
  } catch (err) {
    // When we aborted due to the timeout, surface the typed timeout error
    // regardless of what the runtime turned the abort into (some reject with a
    // DOMException 'AbortError' rather than our reason).
    if (timedOut) throw new FetchTimeoutError(timeoutMs);
    throw err;
  } finally {
    clearTimeout(timer);
    if (callerSignal) callerSignal.removeEventListener('abort', onCallerAbort);
  }
}
